import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SxProps } from "@mui/material";
// Types
import Reducers from "app/types/Reducers";
// Services
import LocalStorageService from "app/services/LocalStorage.service";

interface IPreviewPage {
  dataId?: number | string;
  documentId: number;
  pageNum: number;
  bboxes?: any;
  bb?: string;
  linkIndex?: number;
  sx?: SxProps;
}

interface IState {
  trialMode: boolean;
  workspacePageIds: Record<number, string>;
  calendarDates: Record<number, string>;

  requestedUrl: string | null;
  pagesInRow: string;
  gridResizerWidth: number;

  availableLanguages: string[];
  language: string;

  // Files
  filesSidebarOpen: boolean;
  workspaceSidebarOpen: boolean;
  aiChatSidebarOpen: boolean;
  // Search
  searchSidebarOpen: boolean;
  // Invoice
  invoicesSidebarOpen: boolean;
  // Labeling tool sidebar open
  labellingToolsSidebarOpen: boolean;

  previewPageOpen: boolean;
  previewPage: IPreviewPage | null;
  referencePage: IPreviewPage | null;

  focusModeEnabled: boolean;
};

const initialState:IState = {
  trialMode: LocalStorageService.getTrialMode(),
  workspacePageIds: LocalStorageService.getWorkspacePageIds(),
  calendarDates: LocalStorageService.getCalendarDates(),

  requestedUrl: null,
  pagesInRow: 'auto',
  gridResizerWidth: LocalStorageService.getGridResizerWidth(),

  availableLanguages: ['en', 'es'],
  language: LocalStorageService.getLanguage(),

  // Files
  filesSidebarOpen: LocalStorageService.getBatchesSidebarOpen('files'),
  workspaceSidebarOpen: LocalStorageService.getBatchesSidebarOpen('workspace'),
  aiChatSidebarOpen: true,
  // Search
  searchSidebarOpen: true,
  // Invoices
  invoicesSidebarOpen: true,
  // Labeling
  labellingToolsSidebarOpen: false,

  previewPageOpen: false,
  previewPage: null,
  referencePage: null,

  focusModeEnabled: false
};

const slice = createSlice({
  name: Reducers.AppUi,
  initialState,
  reducers: {
    toggleTrialMode: (state:IState) => {
      const trialMode = !state.trialMode;
      state.trialMode = trialMode;
      LocalStorageService.setTrialMode(trialMode);
    },

    setWorkspacePageId: (state:IState, action:PayloadAction<{ caseId:number, pageId:string }>) => {
      const { caseId, pageId } = action.payload;
      const workspacePageIds = {
        ...state.workspacePageIds,
        [caseId]: pageId
      };
      state.workspacePageIds = workspacePageIds;
      LocalStorageService.setWorkspacePageIds(workspacePageIds);
    },

    setCalendarDate: (state:IState, action:PayloadAction<{ caseId:number, date:string }>) => {
      const { caseId, date } = action.payload;
      const calendarDates = {
        ...state.calendarDates,
        [caseId]: date
      };
      state.calendarDates = calendarDates;
      LocalStorageService.setCalendarDates(calendarDates);
    },

    setRequestedUrl: (state:IState, action:PayloadAction<string | null>) => {
      state.requestedUrl = action.payload;
    },
    setPagesInRow: (state:IState, action:PayloadAction<string>) => {
      state.pagesInRow = action.payload;
    },
    setGridResizerWidth: (state:IState, action:PayloadAction<number>) => {
      state.gridResizerWidth = action.payload;
      LocalStorageService.setGridResizerWidth(action.payload);
    },
    setLanguage: (state:IState, action:PayloadAction<string>) => {
      state.language = action.payload;
      LocalStorageService.setLanguage(action.payload);
    },

    toggleBatchesSidebar: (state:IState, action:PayloadAction<{ type: 'files' | 'workspace', value?:boolean }>) => {
      const { type, value } = action.payload;
      const open = value ?? !state[`${type}SidebarOpen`];
      state[`${type}SidebarOpen`] = open;
      LocalStorageService.setBatchesSidebarOpen(type, open);
    },

    toggleAIChatSidebar: (state:IState) => {
      state.aiChatSidebarOpen = !state.aiChatSidebarOpen;
    },

    toggleSearchSidebarOpen: (state:IState) => {
      state.searchSidebarOpen = !state.searchSidebarOpen;
    },

    toggleInvoicesSidebarOpen: (state:IState) => {
      state.invoicesSidebarOpen = !state.invoicesSidebarOpen;
    },

    toggleLabellingToolsSidebarOpen: (state:IState) => {
      state.labellingToolsSidebarOpen = !state.labellingToolsSidebarOpen;
    },

    setPreviewPageOpen: (state:IState, action:PayloadAction<boolean>) => {
      state.previewPageOpen = action.payload;
    },
    setPreviewPage: (state:IState, action:PayloadAction<IPreviewPage | null>) => {
      const sx = action.payload?.sx || state.previewPage?.sx || {};
      state.previewPage = action.payload ? { ...action.payload, sx } : null;
    },
    setReferencePage: (state:IState, action:PayloadAction<IPreviewPage | null>) => {
      const sx = action.payload?.sx || state.referencePage?.sx || {};
      state.referencePage = action.payload ? { ...action.payload, sx } : null;
    },

    setFocusMode: (state:IState, action:PayloadAction<boolean>) => {
      state.focusModeEnabled = action.payload;
    },

    setInitialField: <IStateKey extends keyof IState>(state:IState, action:PayloadAction<IStateKey>) => {
      state[action.payload] = initialState[action.payload];
    },
    resetState: () => initialState
  }
});

export const AppUIActions = slice.actions;

export default slice.reducer;
